const en = {
  brand: "Veyra",
  common: {
    demo: "Demo",
    demoLong: "Demo content",
    demoModeTitle: "Demo content",
    demoModeBody: "Items marked as Demo are illustrative examples included in the initial state. They do not represent real generations or official identities of your characters or products.",
    pending: "Integration pending",
    save: "Save",
    saved: "Saved",
    cancel: "Cancel",
    delete: "Delete",
    duplicate: "Duplicate",
    create: "Create",
    close: "Close",
    back: "Back",
    next: "Continue",
    skip: "Skip for now",
    finish: "Finish",
    open: "Open",
    select: "Select",
    selected: "Selected",
    edit: "Edit",
    apply: "Apply",
    applied: "Applied",
    type: "Type",
    prompt: "Prompt",
    project: "Project",
    status: "Status",
    noProject: "No project",
    untitled: "Untitled",
    loadError: "Your saved data could not be loaded. Check your connection and try again.",
    loadError401: "Your session has expired. Please sign in again.",
    loadErrorServer: "Server error loading your data. Please try again.",
    saveError: "The change could not be saved. Check your connection.",
    saveError401: "Your session has expired. The latest changes were not saved. Please sign in again.",
    saveErrorServer: "Server error while saving. Please try again.",
    confirm: "Confirm",
    understood: "Got it",
    loading: "Loading…",
    retry: "Retry",
    none: "None",
    search: "Search",
    all: "All",
    untitled: "Untitled",
    id: "ID",
    createdAt: "Created",
    updatedAt: "Updated",
    version: "Version",
    original: "Original",
    prompt: "Prompt",
    references: "References",
    status: "Status",
    type: "Type",
    name: "Name",
    description: "Description",
    handle: "Handle",
    format: "Format",
    project: "Project",
    scenes: "Scenes",
    assets: "Assets",
    noProject: "No project",
    noProjectHint: "The result will be saved to the general Gallery; it is not lost.",
    notConnected: "Not connected",
    notConfigured: "Not configured",
    downloadSuccess: "Download started",
    downloadError: "Failed to download original file",
    unknown: "Unknown",
    estimated: "Estimated",
    confirmed: "Confirmed",
    remove: "Remove",
    add: "Add",
    warning: "Warning",
    comingSoon: "Coming soon",
  },
  stepStatus: { completed: "Completed", skipped: "Skipped", pending: "Pending" },
  generation: {
    completedToast: "Generation completed",
    failedToast: "Generation failed",
    submitError: "Error submitting generation",
    budgetInsufficient: "Insufficient budget for this generation",
    budgetNotConfigured: "No monthly budget limit is configured. Set a limit in Settings › Budget before generating.",
    budgetSettingsLink: "Settings › Budget",
    uncertainState: "Uncertain submission state: the provider may have received the request. It was not re-sent to avoid a double charge. Check the task status in the tasks panel.",
    blockedNoKey: "No API key configured. The task is saved as blocked.",
    maxRefs: "Maximum references reached for this phase",
    providerNote: "Seedream 5.0 Pro via Apimart",
  },
  connections: {
    status: { not_configured: "No key configured", configured: "Key configured", verified: "Connection verified", error: "Error" },
    none: "No connection",
    required: "Add a connection in Settings → Connections to continue.",
    noneOperational: "You have connections created, but none have a key configured or operational with a compatible model yet.",
    shortCount: (n) => `${n} unconfigured`,
    capabilities: { image: "Image", video: "Video", text: "Text", audio: "Audio/voice" },
    stages: { created: "Created", unconfigured: "Unconfigured", unverified: "Unverified", operational: "Operational" },
    lifecycleNote: "A connection moves through these stages: Created → Unconfigured (no key) → Unverified (key stored, not tested yet) → Operational (verified and ready).",
  },
  nav: {
    home: "Home",
    projects: "Projects",
    characters: "Characters & References",
    gallery: "Gallery",
    tasks: "Tasks",
    admin: "Admin Panel",
    settings: "Settings",
    onboarding: "Initial setup",
    workspace: "Workspace",
    collapse: "Collapse menu",
    expand: "Expand menu",
    menu: "Menu",
    home_icon_hint: "Go to home",
  },
  banners: {
    demoTitle: "You're viewing demo data",
    demoBody:
      "Images and text marked as Demo are illustrative examples. There are no real generations, no connected providers, and no official identity for your characters or products.",
    onboardingIncomplete: "Initial setup incomplete",
    onboardingResume: "Resume",
    noConnection: "Generation disabled: no connection is configured. Add one in Settings → Connections.",
    noProvider: "Generation disabled: no connection is configured. Add one in Settings → Connections.",
    localOnly:
      "Your data is saved in your database, protected by your session. Images you upload are stored on the server, also protected.",
  },
  pendingDialog: {
    title: "Action pending integration",
    body: () =>
      "This action requires an integration that isn't connected yet. In this mode no real calls are made and no results are simulated.",
    registered: "The request was logged in Tasks as blocked so you can review it.",
    notRegistered: "No task was registered and no charge was made.",
    viewTasks: "View tasks",
  },
  home: {
    title: "Home",
    greeting: (ws) => ws || "Studio",
    quick: "Quick tools",
    createImage: "Create image",
    createImageDesc: "Prompt, references and format. The current model (Seedream 5.0 Pro) generates 1 image per run.",
    createVideo: "Create video",
    createVideoDesc: "Start/end image, duration and references depending on the model.",
    editImage: "Edit image",
    editImageDesc: "Instructions over an existing image; never overwrites the original.",
    upscaler: "Upscaler",
    upscalerDesc: "AI resolution boost. The available service will be verified.",
    newProduction: "New production",
    newProductionDesc: "Project with connected storyboard, canvas and editor.",
    recent: "Recent projects",
    recentEmpty: "No projects yet. Create your first production.",
    tasks: "Tasks",
    tasksEmpty: "No tasks logged yet.",
    viewAll: "View all",
    connections: "Connections",
    imageVideo: "Image & video",
    textAssistant: "Text assistant",
    budget: "Budget",
    budgetNoLimit: "No limit configured",
    spent: "Measured real spend",
    spentNone: "No data — no real calls",
    budgetSummary: (limit, pct) => `US$ ${limit} / month · alert at ${pct}%`,
  },
  onboarding: {
    title: "Initial setup",
    subtitle: "A single owner. Can be resumed and changed later in Settings.",
    steps: ["Private access", "Workspace", "Connections", "Assistant", "Budget", "First project"],
    access: {
      title: "Private access",
      body:
        "Veyra is a private studio. Real authentication, closing initial sign-up and secure storage arrive later. For now this step is only reserved; no password is stored.",
      owner: "Owner name",
      ownerPh: "Michael",
      note: "No password will be requested until secure sign-in exists.",
      language: "Interface language",
      languageHint: "Changes Veyra's menus, buttons and messages. It does not translate your prompts, scripts or future narration.",
    },
    workspace: {
      title: "Workspace",
      name: "Workspace name",
      namePh: "Veyra Studio",
      es: "Español",
      en: "English",
      brandNote: "Veyra remains the brand; the Workspace is your working space.",
      formatPref: "Preferred format",
      formatPrefHint: "Used as the default format for new productions. You can change it on every generation.",
      resolutionPref: "Default image resolution",
      resolutionPrefHint: "Starting point in the image generator; can be adjusted on every generation.",
    },
    connections: {
      title: "Connections",
      body:
        "Connections link Veyra to image, video, text or audio/voice generation providers. Apimart is the first available provider, for image and video; ElevenLabs appears prepared for audio/voice as a pending integration. The real key will be stored securely and models will be verified against the official docs later.",
      add: "Add sample connection",
      explore: "You can explore the interface without creating a connection. Creating one only enables the generation forms; real generation also needs a verified connection and a compatible model, which this phase does not allow yet.",
      createdNote: "Connection created without a key. Status: No key configured.",
    },
    assistant: {
      title: "Text assistant",
      body:
        "The script/prompt assistant needs a connection compatible with text. None of your connections support text yet. It can be configured once a verified text connection exists.",
      provider: "Text connection",
      providerPh: "Defined after verifying capabilities",
      disabled: "Not available yet.",
    },
    budget: {
      title: "Budget & alerts",
      body: "Indicative monthly spend limit for generation. No rates or prices are charged; real usage will be measured once calls exist.",
      limit: "Monthly limit (USD)",
      alert: "Alert at (%)",
      noLimit: "No limit",
      block: "Block new generations once the budget is reached",
      blockHint: "Saved as a preference. Real spend blocking will activate together with the provider connection; for now there are no charges to block.",
    },
    first: {
      title: "First project",
      body: "Create your first production or go to a quick tool.",
      projectName: "Project name",
      projectPh: "Product spot — summer",
      createAndOpen: "Create project and open",
      goHome: "Go to Home",
    },
    done: "Setup completed",
    exploreWithout: "Explore without finishing →",
    accessComingSoon: "Coming soon: authentication, session and closing of the initial sign-up.",
  },
  guestOnboarding: {
    title: "Welcome to VEYRA",
    subtitle: "Set up your personal workspace in three simple steps.",
    steps: ["Your Studio", "Connect Apimart", "Control Your Budget & Start"],
    step1: {
      title: "Your Studio",
      subtitle: "Customize how you appear in the studio and your interface language.",
      nameLabel: "Your name in the studio",
      namePlaceholder: "Your name or alias",
      languageLabel: "Interface language",
      languageHint: "You can change the language at any time from Settings.",
    },
    step2: {
      title: "Connect Apimart",
      subtitle: "Configure your personal API key to generate images and videos with your own credits.",
      keyLabel: "Apimart API key",
      keyPlaceholder: "Enter your Apimart API key",
      help: "Your key is encrypted and securely stored in your personal server vault. It is never shared with other studio members.",
      saveNotice: "Saving a key does not verify it. Verification and billing occur during generation.",
      saveBtn: "Save key and continue",
      skipBtn: "Configure later",
      exploreNotice: "You can explore the interface without a key. Generation limits will remain active until a valid key is provided.",
      successSaved: "Apimart key saved and protected in your personal vault.",
      emptyKeyError: "Please enter a valid key or select 'Configure later'.",
    },
    step3: {
      title: "Control Your Budget & Start",
      subtitle: "Set a personal monthly budget limit for your generations and start creating.",
      limitLabel: "Monthly limit (USD)",
      limitPlaceholder: "e.g., 50.00",
      limitHint: "You can configure it later. To generate you will need an Apimart key and a monthly limit.",
      projectLabel: "First production name",
      projectPlaceholder: "My first project",
      createProjectBtn: "Create my first project",
      exploreBtn: "Explore VEYRA",
    },
  },
  projects: {
    title: "Projects",
    new: "New production",
    empty: "No projects",
    emptyBody: "A production brings together script, scenes, references, canvas and versions.",
    scenesCount: (n) => `${n} ${n === 1 ? "scene" : "scenes"}`,
    assetsCount: (n) => `${n} ${n === 1 ? "asset" : "assets"}`,
    deleteConfirm: "Delete this project and all its scenes, nodes and connections?",
    formatLabel: "Format",
  },
  project: {
    views: { storyboard: "Storyboard", canvas: "Canvas", editor: "Editor", resources: "Resources" },
    projectResources: "Project resources",
    assistant: "Assistant",
    library: "Library",
    showAssistant: "Show Creative Director",
    hideAssistant: "Hide Creative Director",
    showLibrary: "Show library",
    hideLibrary: "Hide library",
    notFound: "Project not found",
    notFoundBody: "The project does not exist or was deleted.",
    backToProjects: "Back to projects",
    selectedScene: "Selected scene",
    noSelection: "No selection",
  },
  storyboard: {
    addScene: "New scene",
    createFirstScene: "Create first shot",
    empty: "No scenes",
    emptyBody: "Create the first scene or ask the assistant for a sample proposal.",
    emptyBodyGuest: "Create your first shot to start structuring your project.",
    sceneN: (n) => `Scene ${n}`,
    moveUp: "Move up",
    moveDown: "Move down",
    approve: "Approve",
    unapprove: "Remove approval",
    toReview: "Mark for review",
    toDraft: "Back to draft",
    status: { draft: "Draft", review: "In review", approved: "Approved" },
    detail: "Scene detail",
    showDetail: "Show scene details",
    hideDetail: "Hide scene details",
    title: "Title",
    prompt: "Prompt",
    currentPrompt: "Shot prompt",
    promptHelp: "Describe what should appear and happen in this shot. You can write it or prepare it with the Creative Director.",
    selectedMediaOrigin: "Selected media origin",
    originPrompt: "Original generation prompt",
    originPromptNotAvailable: "Origin prompt not available",
    originModel: "Model",
    originProvider: "Provider",
    promptMismatchNotice: "The current scene prompt is different from the one used to generate this media",
    promptPh: "E.g.: @elena examines old blueprints on the library desk illuminated by candlelight",
    notes: "Continuity notes",
    notesHelp: "Note details that must remain consistent between shots: wardrobe, objects, position, lighting, or action state.",
    notesPh: "E.g.: bag on left shoulder; the remote is still tucked away; same afternoon lighting",
    sceneAssets: "Scene media",
    sceneMedia: "Scene media",
    noAssets: "No media associated yet.",
    noSceneMedia: "No media associated yet.",
    chooseFromLibrary: "Choose from library",
    chooseFromGallery: "Choose from gallery",
    uploadFromDevice: "Upload from my device",
    scopeThisProject: "This project",
    scopeAllGallery: "All my gallery",
    filterAllMedia: "All",
    filterImages: "Images",
    filterVideos: "Videos",
    uploadProgress: (pct) => `Uploading... ${pct}%`,
    uploadError: "Failed to upload image. Please try again.",
    retryUpload: "Retry upload",
    retryAssign: "Retry assignment",
    assignErrorPreserved: "Could not assign to scene, but the file was preserved in your gallery.",
    fileTooLarge10MB: "Image exceeds maximum allowed size (10 MB).",
    unsupportedImageFormat: "Unsupported format. Use PNG, JPEG or WebP.",
    chooseMediaFromLibrary: "Choose media from library",
    chooseFromLibraryTitle: (sceneName) => `Choose media for ${sceneName}`,
    assignToThisScene: "Assign to this scene",
    assignToSceneNamed: (sceneName) => `Assign to ${sceneName}`,
    assignedToSceneSuccess: (sceneName) => `Media assigned to ${sceneName}`,
    replaceMediaTitle: (sceneName) => `Replace active media for ${sceneName}?`,
    replaceMediaBody: "The scene already has an active media. Assigning this new media will keep the previous one in the project library.",
    replaceMediaConfirm: "Replace media",
    directGenerationSoon: "Direct generation coming soon",
    directGenerationHint: "Direct generation from the scene is not integrated yet. You can open the shot in the Editor to work with it.",
    useThisMedia: "Use this media",
    selectedMediaBadge: "Selected",
    generate: "Generate image for this scene",
    openInEditor: "Open in editor",
    openInCanvas: "View in canvas",
    deleteConfirm: "Delete this scene? Its canvas nodes and connections will be removed too.",
    trash: "Trash",
    sceneDeleted: "Scene deleted",
    undo: "Undo",
    restore: "Restore",
    restoredSuccess: "Scene restored",
    trashTitle: "Project Trash",
    trashSubtitle: "Deleted scenes from this project are kept here so you can recover them at any time.",
    trashEmpty: "Trash is empty",
    trashSyncError: "Could not sync trash with the server.",
    trashSyncRetry: "Retry connection",
    deletedAt: (date) => `Deleted on ${date}`,
    pickReferences: "Pick references",
    noReferences: "No references",
    permanentDelete: "Delete permanently",
    emptyTrash: "Empty trash",
    purgeConfirmTitleSingle: (title) => `Delete "${title}" permanently?`,
    purgeConfirmTitleBatch: (count) => `Empty trash (${count} scenes)?`,
    purgeConfirmWarning: "This action is irreversible. The affected scenes cannot be recovered.",
    purgeDefaultNotice: "By default, only the scenes will be removed from trash. Their images and videos will remain in the Gallery, and characters, objects, and references in the library.",
    deleteFilesCheckbox: "Also delete files used solely by these scenes",
    deleteFilesNote: "Only direct files with no other usage will be deleted. Characters, objects, and reference sheets will never be deleted.",
    deletableFilesCount: (count) => `${count} exclusive file(s) deletable`,
    protectedFilesCount: (count) => `${count} protected file(s) in use elsewhere`,
    spaceFreedEstimate: (space) => `Space to free: ${space}`,
    spaceUnavailable: "Space not available",
    calculatingUsage: "Calculating file usage…",
    purging: "Deleting…",
    purgeSuccessSingle: "Scene permanently deleted",
    purgeSuccessBatch: "Trash emptied successfully",
    purgePartialError: "Scenes were deleted, but some files could not be physically removed. Recorded for retry.",
  },
  canvas: {
    addNode: "Add node",
    nodeTypes: { text: "Text", reference: "Reference", image: "Image", video: "Video" },
    empty: "Empty canvas",
    emptyBody: "Add nodes and connect them by dragging from the output port to the input port.",
    hint: "Drag nodes to move them. Drag from ● (output) to ○ (input) to connect. Click a connection to select it; Delete to remove.",
    deleteEdge: "Remove connection",
    deleteNode: "Remove node",
    run: "Run node",
    runHint: "One node per run (v1).",
    invalid: {
      sameNode: "A node cannot connect to itself.",
      duplicate: "That connection already exists.",
      cycle: "That connection would create an execution cycle.",
      type: (a, b) => `Connecting ${a} → ${b} is not allowed.`,
      videoOut: "A video node has no outputs in v1.",
    },
    linkedScene: "Linked scene",
    unlinked: "Not linked",
    textPh: "Text or prompt…",
    pickAsset: "Pick image",
    noAsset: "No image assigned",
    noVideo: "No video generated",
    pickReference: "Pick reference",
    zoom: "Zoom",
    fit: "Reset view",
    edges: (n) => `${n} ${n === 1 ? "connection" : "connections"}`,
    nodes: (n) => `${n} ${n === 1 ? "node" : "nodes"}`,
  },
  editor: {
    empty: "No image selected",
    emptyBody: "Select a scene with an image in the Storyboard or a version in the library.",
    instructions: "Edit instructions",
    instructionsPh: "E.g.: change the background to a concrete wall, keep the lighting…",
    applyInstructions: "Apply instructions",
    before: "Before",
    after: "After",
    compare: "Compare",
    history: "Version history",
    crop: "Crop",
    rotate: "Rotate 90°",
    cropRatios: { "1:1": "1:1", "16:9": "16:9", "9:16": "9:16", "4:5": "4:5" },
    sendUpscale: "Send to Upscaler",
    originalNote: "The original is never overwritten; every operation creates a new version.",
    processing: "Processing…",
    error: "Could not process the image (source unreachable or unsupported format).",
    localOp: "Local operation",
    videoNote: "Video player with native playback controls. Editing and transformation tools are currently available for images.",
    sceneVersions: "Scene versions",
    selectedVersion: "Selected",
    inUse: "In use",
    useInScene: "Use",
    videoType: "Video",
    imageType: "Image",
    untitledVideo: "Untitled video",
    untitledImage: "Untitled image",
    originalFile: "Original file",
    imageToEdit: "Image to edit",
    baseImageChanged: "Base image changed to this version. Instructions will be applied to it.",
    requestedAssetNotFound: "The requested asset does not exist or does not belong to this project.",
    lastResultTitle: "Latest completed result",
    generatedFrom: "Generated from",
    viewResult: "View result in editor",
  },
  characters: {
    title: "Character Library",
    subtitle: "Reusable characters, products, scenarios, and textures with multiple views and canonical references.",
    new: "New Character",
    edit: "Edit Character",
    openSheet: "View Sheet",
    kinds: { character: "Character", product: "Product", scenario: "Scenario", texture: "Texture", vehicle: "Vehicle", prop: "Prop / Object" },
    empty: "No characters",
    emptyBody: "Create your first character to save their physical traits, base wardrobe, and reference views.",
    master: "Master Image",
    masterBadge: "Master",
    setAsMaster: "Set as Master",
    views: "Views",
    viewsCount: (n) => `${n} ${n === 1 ? "view" : "views"}`,
    noMaster: "No master image",
    addView: "Add View",
    pickFromLibrary: "Pick from Library",
    uploadImage: "Upload Image",
    uploading: "Uploading...",
    removeView: "Remove View",
    viewTypes: {
      front: "Front",
      side: "Profile",
      back: "Back",
      expression: "Expression",
      other: "Other",
    },
    identitySection: "Identity & Physical Traits",
    identityPlaceholder: "Apparent age, build, distinctive facial features, hair color, eyes...",
    outfitSection: "Base Wardrobe (separated from identity)",
    outfitPlaceholder: "Default outfit, characteristic colors, footwear or canonical accessories...",
    outfitHint: "Base wardrobe is documented separately to keep physical identity when clothes change across scenes.",
    handleHint: "Unique @handle for prompt mentions (e.g. @sarah_chen).",
    handleCollision: (h) => `Handle @${h} is already in use in this scope. Choose another.`,
    handleRequired: "The @handle is required.",
    nameRequired: "The name is required.",
    projectScope: "Project",
    allProjects: "All Projects",
    searchPlaceholder: "Search by name or @handle...",
    deleteConfirm: "Delete this character? It will be removed from linked scenes, but images will remain in gallery.",
    discardConfirmTitle: "Discard unsaved changes?",
    discardConfirmBody: "You have unsaved modifications. If you leave now, your changes will be lost.",
    continueEditing: "Continue Editing",
    discardChanges: "Discard Changes",
    referenceNote: "The sheet can store multiple views. Each generation sends at most 2 references (or fewer based on model limits).",
    mentionNote: "The @handle mention documents character presence in the prompt. It does not replace reference selection.",
    requestView: "Request New View",
    usedIn: (n) => `Used in ${n} ${n === 1 ? "scene" : "scenes"}`,
    prefilledFromGallery: "Master image pre-filled from the Gallery.",
    useAsProjectCover: "Use as Project Cover",
    useAsProjectCoverNamed: (name) => `Use as cover of: ${name}`,
    currentProjectCover: "Current Project Cover",
    currentProjectCoverNamed: (name) => `Current cover of ${name}`,
    coverUpdated: (name) => `Image set as cover of "${name}".`,
    saveBeforeCover: "Save character changes before assigning project cover.",
    assignProjectBeforeCover: "Assign a project to the character before setting its cover.",
    alreadyProjectCover: (name) => `This image is already the cover of "${name}".`,
    foreignAssetCoverError: "The asset does not belong to or is not authorized for this project.",
    analyzeSectionTitle: "Autocomplete sheet from image",
    analyzeImageBtn: "Analyze image and complete sheet",
    analyzingImage: "Analyzing image...",
    noVisionModelNotice: "Autocompleting sheet via image analysis requires a connection with vision capabilities (ModelCapability.VISION). There are currently no vision models configured in Settings > LLM Connections.",
    modelCostEstimate: (model, cost) => `Model: ${model} · Estimated cost: US$${cost}`,
    proposalTitle: "Visual analysis proposal",
    proposalSubtitle: "Observable traits directly from the image. Review and accept each section:",
    applyToIdentity: "Apply to Identity",
    applyToOutfit: "Apply to Wardrobe",
    discardProposal: "Discard proposal",
    appliedToIdentity: "Identity updated with visual proposal.",
    appliedToOutfit: "Wardrobe updated with visual proposal.",
    imageChangedDuringAnalysis: "The analyzed image changed during processing; the previous proposal was discarded.",
    visibleTraitsNotice: "Describes only visible traits observed in the image.",
    selectViewsPrompt: "Choose saved views to use as visual generation references.",
    noViewsTitle: "No saved views",
    noViewsDesc: "This character has no saved views in the library. You can add views from the character sheet or use manual references.",
    noViewAttachedNote: "The @handle mention will remain in the prompt, but no empty reference will be attached.",
    savedViewsCount: (n) => `${n} ${n === 1 ? "available view" : "available views"}`,
    maxReferencesAllowed: (n) => `Total maximum of ${n} references`,
    linkViewsButton: "Select views",
    linkViewsButtonCount: (n) => `Link ${n} view${n > 1 ? "s" : ""}`,
  },
  gallery: {
    title: "Gallery",
    subtitle: "All image and video versions in the workspace.",
    empty: "Gallery is empty",
    emptyBody: "Generation results and edited versions will appear here.",
    filters: { all: "All", image: "Images", video: "Videos" },
    projectFilterLabel: "Project",
    projectFilterAllHint: "Showing assets from all projects, including those with no project.",
    openEditor: "Open in editor",
    openEditorNoProject: "Only available for assets that belong to a project.",
    openEditorVideo: "The editor only supports images.",
    download: "Download",
    useAsReference: "Use as reference",
    upscale: "Upscaler",
    upscaleVideoDisabled: "Video upscaler not available in this phase.",
    source: { demo: "Demo", upload: "Upload", generation: "Generation", edit: "Local edit" },
    sourceLabel: "Source",
    sceneLabel: "Scene",
    technicalInfo: "Technical information",
    originalFile: "Original file",
    assetId: "Asset ID",
    dimensions: "Dimensions",
    realDimensions: "Real dimensions",
    aspectRatio: "Aspect ratio",
    notAvailable: "Not available",
    sourceUpload: "Upload",
    sourceGeneration: "Generation",
    sourceUnknown: "Unspecified",
    uploadedAssetTitle: "Uploaded image",
    uploadedAssetNotice: "This asset was uploaded manually and does not originate from a generation job.",
    generationJobNotFoundTitle: "Generation (job not found)",
    generationJobNotFoundNotice: "This asset originates from a generation, but no producer job was found in local history.",
    unknownSourceTitle: "Unspecified origin",
    unknownSourceNotice: "No source information is available for this asset.",
    selectMode: "Select",
    selectAll: "Select all",
    deselect: "Deselect",
    deleteSelected: (n) => `Delete selected (${n})`,
    deleteSelectedDate: (n) => `Delete selected from this date (${n})`,
    dateSelectedCount: (n) => `(${n} selected)`,
    totalSelectedCount: (n) => `${n} selected`,
    floatingActionsTitle: "Selection actions",
  },
  tasks: {
    title: "Tasks",
    subtitle: "Generation, edit and upscale history for this workspace.",
    empty: "No tasks",
    emptyBody: "Generation, edit and upscale requests will appear here with their status.",
    status: {
      blocked: "Blocked",
      pending: "Pending",
      running: "Running",
      done: "Done",
      failed: "Failed",
      uncertain: "Uncertain",
      cancelled: "Cancelled",
    },
    kinds: { image: "Image", video: "Video", edit: "Edit", upscale: "Upscale", assistant: "Assistant", canvas: "Canvas node" },
    blockedReason: "Provider not connected. Nothing was sent or charged.",
    blockedReasonNoKey: "Connection without a configured key. Nothing was sent or charged.",
    uncertainReason: "Uncertain submission state: the provider may have received the request. It was not re-sent to avoid a possible double charge. Check the provider dashboard if you have access.",
    requestedSize: "Requested size",
    simTag: "Simulated",
    model: "Model",
    modelUnknown: "Not verified",
    cost: "Cost",
    costUnknown: "No data",
    costEstimated: (v) => `Estimated US$ ${v}`,
    costConfirmed: (v) => `Confirmed US$ ${v}`,
    countHint: "Blocked tasks: the connection has no API key configured yet.",
    clear: "Delete blocked tasks",
    clearConfirm: (n) => `${n} blocked task${n !== 1 ? "s" : ""} will be permanently deleted. This cannot be undone.`,
    demoTag: "Demo task",
  },
  tools: {
    navImage: "Image",
    navEdit: "Edit image",
    navVideo: "Video",
    navUpscale: "Upscaler",
    back: "Back",
    guestKeyMissing: "Configure your Apimart key to generate",
    guestBudgetMissing: "Set a monthly limit before generating",
    guestBudgetInsufficient: "Monthly budget insufficient for this generation",
    catalogLoadError: "Error checking server connections. Could not verify your key status.",
    catalogErrorRetry: "Error checking connections. Please retry.",
    loadingConnections: "Loading connections...",
    connectionsError: "Error checking connections (click retry)",
    budgetInsufficient: (avail, cost) => `Insufficient budget: available ($${avail}) does not cover estimated cost ($${cost}).`,
    image: { title: "Create image", cta: "Generate image" },
    video: { title: "Create video", cta: "Generate video" },
    edit: { title: "Edit image", cta: "Apply edit" },
    upscale: { title: "Upscaler", cta: "Upscale resolution" },
    promptPh: "Describe what you want to create…",
    references: "References (@handles)",
    referencesEmpty: "Add reference thumbnails from Characters or the gallery.",
    format: "Format",
    formatMore: "Extended formats",
    formatAuto: "Auto",
    quantity: "Quantity",
    resolution: "Resolution",
    resolutionIncompatible: "Not available on this model",
    model: "Model",
    connection: "Connection",
    connectionPick: "Pick a connection",
    modelNote: "",
    modelChangedWarning: "When you switch models, check format and resolution: they may no longer be compatible. Veyra never changes them silently.",
    modelChangedReset: "Model changed: format and resolution automatically adjusted to compatible values.",
    incompatibleSelection: "The chosen format/resolution combination is not compatible with this model. Adjust it before generating.",
    costEstimateTitle: "Estimated cost",
    costEstimated: (v) => `Estimated: ${v}`,
    costAvailableEstimated: (a, c) => `Available ${a} · this generation ${c}`,
    costBudgetLimit: "Configured monthly limit",
    costBudgetNone: "No limit configured",
    costBudgetAvailable: "Available budget",
    costUnverified: "unverified",
    costUnverifiedTitle: "This amount is not confirmed by a verified primary source",
    costExtraRef: (v) => `+ ${v} per additional reference (2nd onward)`,
    costDemoNote: "Estimated price, not verified. Internal source; pending confirmation in Apimart official docs.",
    phase5Notice: "Not available in this phase (Phase 5).",
    phase5Body: "Video upscaler is not available in this phase.",
    upscaleModel: "Upscale model",
    upscaleScale: "Scale factor",
    upscaleScaleOption: (n) => `${n}× (${n === 2 ? "double" : "quadruple"} resolution)`,
    upscaleOriginalLabel: "Source image",
    upscaleNoSource: "Select source image from gallery",
    upscalePickSource: "Change source image",
    upscaleReserveNotice: (usd) => `Price not published · Reserve: $${usd} (conservative). Actual cost will be reconciled after completion.`,
    upscaleNoPriceBlock: "Price unavailable — the reserve protects your budget. The actual charge will be reconciled on completion.",
    upscaleSubmit: "Send to upscaler",
    upscaleSubmitting: "Sending…",
    upscaleSuccessTitle: "Upscale submitted",
    upscaleSuccessBody: "Your image is being upscaled. It will appear in Gallery when ready.",
    upscalePreservedNote: "The original is preserved. Upscale creates a new asset.",
    simulateLabel: "Simulate completion (test mode, no key)",
    simulateSuccess: "Simulation complete — asset available in Gallery",
    refsCap: "Max. 2 references per generation.",
    refsLimitModalTitle: "Reference limit reached",
    refsLimitModalSubtitle: "The current model allows a maximum number of references per generation. Choose which ones to keep:",
    selectRefsToKeep: "Choose references to keep:",
    manualRef: "Manual",
    candidateRef: "New",
    overLimitWarning: (n) => `You have selected more than ${n} references. Deselect some to continue.`,
    confirmKeepRefs: (n) => `Keep ${n} reference${n > 1 ? "s" : ""}`,
    changeView: "Change",
    modelRefsIncompatible: (m) => `The selected model (${m}) does not support image references. Remove the references or choose a compatible model.`,
    videoRefsIncompatible: "Video generation does not support additional reference images. Remove the references or use Start Frame / End Frame.",
    editOriginalPreserved: "The original is preserved. Editing generates a new asset.",
    whatToChange: "What do you want to change?",
    additionalReferences: "Additional references",
    additionalReferencesNote: "Add reference images to guide characters, style, or elements.",
    originalPreservedBrief: "The original is preserved",
    originalPreservedFull: "The original is preserved; editing creates a new asset.",
    uploadAnother: "Upload another image",
    moreActions: "More actions",
    project: "Save to project",
    sourceImage: "Source image",
    pickSource: "Pick from gallery",
    replaceSource: "Replace",
    removeSource: "Remove",
    expand: "Expand",
    clickToExpand: "Click to expand",
    noSourceSelected: "No source image selected",
    selectSourceHelp: "Choose an image from the gallery or upload one from your computer to begin editing.",
    uploadTest: "Upload image",
    uploadTooLarge: "The image exceeds the maximum allowed size (10 MB).",
    uploadUnsupported: "Unsupported format. Use JPG, PNG or WebP.",
    uploadFailed: "The image could not be uploaded. Please try again.",
    uploadEmpty: "The file is empty.",
    uploadTestNote: "Images are uploaded to your protected server (max 10 MB, JPG/PNG/WebP). Edits create new versions without overwriting the original.",
    duration: "Duration",
    durationNote: "Seconds. Depends on the chosen model.",
    startFrame: "Start frame",
    endFrame: "End frame",
    videoMode: "Mode",
    videoModes: { text_to_video: "Text to video", image_to_video: "Start image to video", first_last_frame: "First/last frame", motion_control: "Motion control" },
    motionVideo: "Motion reference video",
    frameFormatMismatch: "The chosen image's format doesn't match the output format. Adjust the format or pick another image; Veyra never crops silently.",
    cheapTest: "Cheap test",
    cheapTestApply: "Apply cheap setup",
    cheapTestApplied: (res, dur) => `Cheap setup applied: ${res} · ${dur}s · 1 output (demo price, no verified rates).`,
    cheapTestNote: "Uses the lowest-cost compatible combination within the chosen model. Does not switch models or generate automatically.",
    nativeNote: "Native generation. A later resolution upscale is a separate action (Upscaler); a video upscaler is not implemented in this review.",
    factor: "Factor",
    factorNote: "Whether the connected provider offers real upscaling will be verified; interpolated scaling is not the same as AI enhancement.",
  },
  settings: {
    title: "Settings",
    sections: { profile: "My profile", workspace: "Workspace", connections: "Connections", budget: "Budget", data: "Data", about: "About" },
    workspaceName: "Workspace name",
    ownerName: "Owner",
    language: "Language",
    formatPref: "Preferred format",
    resolutionPref: "Default resolution",
    connectionsBody: "Real keys will be stored securely later. For now there are no key fields enabled; you can prepare each connection's name and provider.",
    connectionsEmpty: "No connections yet.",
    connectionsEmptyBody: "Add a connection to start setting up the generator. Enabling real generation will also require that connection to be verified with a compatible model; that isn't possible yet in this phase.",
    addConnection: "Add connection",
    connectionName: "Connection name",
    connectionNamePh: "Apimart — production",
    connectionProvider: "Provider",
    connectionProviderNote: "Apimart is the first available provider, for image and video. ElevenLabs is prepared for audio/voice as a pending integration; it does not yet support creating real or demo connections.",
    providerPendingNote: "This provider is marked as a pending integration: connections can't be created yet, real or demo.",
    deleteConnection: "Delete connection",
    deleteConnectionConfirm: "Delete this connection? Tasks that reference it will be left without a connection.",
    ownerRoleLabel: "Owner",
    guestRoleLabel: "Guest",
    budgetLimit: "Monthly limit (USD)",
    budgetAlert: "Alert (%)",
    budgetBlock: "Block new generations once the budget is reached",
    budgetBlockToggleLabel: "Enable",
    budgetBlockHint: "Preference saved for when the provider is connected. It does not block anything operational yet.",
    budgetBreakdownTitle: "Projected spend by category",
    budgetBreakdown: { image: "Images", video: "Video", assistant: "Assistant", audio: "Audio/voice" },
    runningTasksLabel: "Tasks in progress",
    resetDemo: "Restore demo data",
    resetDemoBody: "Replaces the current state with the demo fixture. Local changes will be lost.",
    clearAll: "Clear all",
    clearAllBody: "Fully empties the local workspace (including initial setup).",
    confirmReset: (n) => `Restore demo data? ${n} real-work item${n !== 1 ? "s" : ""} will be lost. This cannot be undone.`,
    confirmClear: (n) => `Clear all data? ${n} item${n !== 1 ? "s" : ""} (projects, scenes, assets, tasks) will be removed. This cannot be undone.`,
    sessionTitle: "Session",
    logoutHint: "Signs you out on this device. You'll need to sign in again with your email and password.",
    passwordLabel: "Password",
    importLocal: "Import data from this browser",
    importLocalBody: "We found a workspace saved in this browser (from before the database was enabled). Import it to replace your current data, or ignore it if you don't need it.",
    importLocalButton: "Import now",
    confirmImport: "Replace your current data with the workspace saved in this browser? This cannot be undone.",
    aboutBody: "Veyra v0.2 — private access and a real database are enabled. No generation providers connected yet.",
    storage: "Storage",
    storageBody: "Database protected by your session; uploaded images are stored on the server.",
    logoNote: "The V mark is a provisional vector reconstruction of the approved logo; a clean official asset is pending.",
    onboardingStepsTitle: "Initial setup status",
  },
  assistant: {
    title: "VEYRA Creative Director",
    subtitle: "Your audiovisual production assistant",
    statusPending: "AI connection pending configuration",
    statusPendingDesc: "Live integration with OpenAI and Claude will be enabled in Checkpoint 15B.",
    providers: {
      openai: "OpenAI",
      claude: "Claude",
      openaiModel: "GPT-4o (Preconfigured)",
      claudeModel: "Claude 3.5 Sonnet (Preconfigured)",
    },
    modes: {
      normal: "Side view (resizable)",
      expanded: "Expand (50%)",
      restore: "Restore size",
      fullscreen: "Full screen",
      exitFullscreen: "Exit full screen",
      collapse: "Hide Creative Director",
      dockTab: "Creative Director",
    },
    welcomeTitle: "Welcome to Creative Director",
    welcomeBody: "Your audiovisual production copilot. Design scripts, optimize cinematic prompts, structure storyboards, and maintain aesthetic continuity across your scenes.",
    quickActionsTitle: "Quick actions",
    quickActions: {
      script: {
        label: "Create script",
        prompt: "Structure an audiovisual script for this project, detailing scenes, suggested camera shots, ambiance, and voiceover/dialogue.",
      },
      prompt: {
        label: "Prepare prompt",
        prompt: "Draft a high-quality cinematic prompt for the selected scene, specifying subject, lighting, camera lens, color palette, and style.",
      },
      improve: {
        label: "Improve scene",
        prompt: "Analyze the current scene and propose visual and narrative enhancements to elevate its cinematic impact and aesthetic coherence.",
      },
      storyboard: {
        label: "Create storyboard",
        prompt: "Design a 3 to 5 key scene sequence for the storyboard: establishing shot, main action development, climax, and closing shot.",
      },
      continuity: {
        label: "Review continuity",
        prompt: "Review visual continuity between scenes: character consistency, lighting, art direction, and temporal flow.",
      },
      plan: {
        label: "Plan production",
        prompt: "Generate a production plan for this project, organizing priority scenes, visual reference needs, and generation sequence.",
      },
    },
    inputPh: "Write an idea, script or question for your production… (Shift+Enter for newline)",
    scriptInputPh: "What story do you want to tell? You can describe your idea or ask me to propose some",
    scriptDraftBanner: "Create script",
    scriptDraftInstruction: "What story do you want to tell? You can describe your idea or ask me to propose some",
    scriptCancel: "Cancel",
    send: "Send",
    newLineHint: "Shift + Enter for newline",
    notConnectedReply: "AI connection pending configuration. Your prompt has been saved to the project thread and will be processed once the model connection is enabled in Checkpoint 15B.",
    demoNote: "Proposals are marked Demo: they are local templates, not model responses. The assistant will connect to a real model later.",
    proposeScenes: "Propose 3 scenes (demo template)",
    proposalTitle: "Scene proposal",
    templateTitle: (n) => `Scene proposal · template ${n}`,
    templateScenes: [
      { title: "Establishing", prompt: "Wide shot of the setting, natural light, rule-of-thirds composition." },
      { title: "Main action", prompt: "Medium shot of the character interacting with the product, focus on hands and product." },
      { title: "Closing", prompt: "Close-up of the product with negative space for the claim." },
    ],
    reviewHint: "Review each proposal before applying it: nothing is added to the Storyboard without your confirmation.",
    applyAll: "Apply all",
    applyOne: "Apply",
    appliedTag: "Applied",
    reviewProposal: "Review proposal",
    closeReview: "Close review",
    confirmAndApplyAll: "Confirm and add all",
    confirmSceneAdd: "Add scene to project?",
    confirmAllScenesPrompt: (n) => `Add ${n} scenes to Storyboard?`,
    dismissProposal: "Dismiss proposal",
    dismissedTag: "Dismissed",
    reactivateProposal: "Reactivate proposal",
    partiallyAppliedTag: (applied, total) => `${applied}/${total} applied`,
    showDetails: "View details",
    hideDetails: "Hide details",
    estimatedDuration: "Estimated duration",
    proposalScenesSummary: (count, seconds) => `${count} shots · ~${seconds}s total`,
    referencedAssets: "Linked references",
    continuityInspectionTitle: "Continuity Alerts",
    continuityInspecting: "Analyzing continuity and cinematic raccord...",
    continuityClean: "No contradictions detected in the reviewed information",
    continuityCleanDesc: "The analysis reviews text and metadata; this does not guarantee visual continuity of the generated frames.",
    continuityError: "Error inspecting project continuity.",
    continuityRetry: "Retry inspection",
    continuityAlertsCount: (n) => `${n} continuity alert${n === 1 ? "" : "s"}`,
    continuityDismiss: "Hide alerts",
    openScene: "Open scene in Storyboard",
    openInStoryboard: "Open scene in Storyboard",
    sceneNotFoundInProject: "The selected scene no longer exists in this project.",
    consultiveNotice: "Consultative supervision: does not modify scenes automatically",
    missingCameraNotice: "Camera parameters not specified in prompt. Remains unknown (neither static nor fixed is assumed).",
    unstructuredCameraNotice: "No structured camera parameters; prompt contains textual indications.",
    empty: "No messages yet. Pick a quick action or write a request for your production.",
    connectionLabel: "Text connection",
    connectionNone: "None of your connections support text yet.",
    connectionConfigure: "Configure text connection",
    connectionHint: "Pick a text-compatible connection if one exists. No provider capability is assumed until it is verified.",
    workingOnScene: (title) => `Working on: ${title}`,
    selectSceneForPrompt: "Select a shot from Storyboard to prepare its prompt",
    selectSceneForImprove: "Select a shot from Storyboard to improve its scene",
    draftSceneMismatchTitle: "The selected shot has changed.",
    draftSceneMismatchDesc: (title) => `The current draft is linked to "${title}". To avoid sending data for another shot, update the draft before sending.`,
    updateDraft: "Update draft",
    unlinkDraft: "Unlink",
    improveInputPh: "What would you like to change or improve in this shot? (e.g., more environment, keep style…)",
    requestFix: "Request correction",
    requestFixPickScene: "Which shot would you like to correct?",
    requestFixForScene: (scTitle) => `Correct in ${scTitle}`,
    continuityDraftNotice: (sceneTitle) => `Working on: ${sceneTitle} · Continuity correction`,
  },
  library: {
    scenes: "Scenes",
    references: "Workspace references",
    versions: "Versions",
    useAsReference: "Use in selected scene",
    removeReference: "Remove from scene",
    openEditor: "Open in editor",
    assignToScene: "Assign to this scene",
    assignToSceneNamed: (sceneName) => `Assign to ${sceneName}`,
    activeInScene: "Active in scene",
    empty: "Nothing to show",
  },
  formats: { "1:1": "Square 1:1", "16:9": "Landscape 16:9", "9:16": "Portrait 9:16", "4:5": "Portrait 4:5", "3:2": "Photo 3:2" },
  ratioLabels: {
    "auto": "Auto",
    "1:1": "1:1 — Square",
    "16:9": "16:9 — Horizontal",
    "9:16": "9:16 — Vertical",
    "4:3": "4:3 — Screen",
    "3:4": "3:4 — Portrait",
    "3:2": "3:2 — Photo",
    "2:3": "2:3 — Portrait",
    "2:1": "2:1 — Wide",
    "1:2": "1:2 — Tall",
    "21:9": "21:9 — Ultrawide",
    "4:5": "4:5 — Portrait",
    "5:4": "5:4 — Square+",
    "1:4": "1:4 — Very Tall",
    "4:1": "4:1 — Very Wide",
    "1:8": "1:8 — Extreme Tall",
    "8:1": "8:1 — Extreme Wide",
  },
  errors: {
    notFound: "Page not found",
    notFoundBody: "This route does not exist in Veyra.",
    goHome: "Go to Home",
    boundary: "Something went wrong rendering this view.",
  },
  auth: {
    setupTitle: "Set up your private access",
    setupSubtitle: "This form only works once. As soon as you create your account, registration closes forever: no one else can claim this Workspace.",
    setupToken: "Installation code",
    setupTokenHint: "The OWNER_SETUP_TOKEN value in backend/.env on your deployment.",
    reclaimTitle: "Recover owner access",
    reclaimSubtitle: "A owner already exists for this Workspace. Enter the installation code (OWNER_SETUP_TOKEN in backend/.env) to set your final email and password. Your projects, scenes, files and connections are not affected.",
    reclaimButton: "Save my credentials",
    reclaiming: "Saving...",
    reclaimLink: "Are you the owner and need to set up your access?",
    no_owner_yet: "No owner exists yet. Use the initial setup form instead.",
    name: "Your name",
    namePh: "Michael De La Rosa",
    email: "Email",
    emailPh: "you@email.com",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    passwordHint: "At least 8 characters.",
    confirmPassword: "Confirm password",
    passwordMismatch: "Passwords don't match.",
    createAccount: "Create my access",
    creating: "Creating...",
    loginTitle: "Private access",
    loginSubtitle: "Sign in to continue with your Workspace.",
    loginButton: "Sign in",
    loggingIn: "Signing in...",
    invalid_credentials: "Incorrect email or password.",
    account_locked: "Too many attempts. Try again in a few minutes.",
    setup_already_complete: "Initial setup is already complete. Sign in instead.",
    invalid_setup_token: "Incorrect installation code.",
    generic: "Something went wrong. Please try again.",
    reclaim_already_used: "Access has already been claimed. To change your password, go to Settings > My profile.",
    wrong_current_password: "Current password is incorrect.",
    changePasswordTitle: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    newPasswordHint: "At least 12 characters.",
    newPasswordTooShort: "New password must be at least 12 characters.",
    changePasswordMismatch: "New passwords don't match.",
    changePasswordSave: "Update password",
    changePasswordSaving: "Updating...",
    changePasswordSuccess: "Password updated. You're still signed in with your new password.",
    logout: "Log out",
    loggingOut: "Logging out...",
    forgotPassword: "Forgot your password?",
    recoveryTitle: "Password Recovery",
    recoverySubtitle: "This studio does not send emails. The recovery code must be generated via administrative server access.",
    recoveryAdminCmd: "Run on the server:",
    recoveryAdminNote: "Compatible with Emergent preview and your Hetzner server. The code expires in 15 minutes and can only be used once.",
    recoveryToken: "Recovery code",
    recoveryTokenPh: "Paste the generated code here",
    recoveryNewPassword: "New password",
    recoveryConfirmPassword: "Confirm password",
    recoveryButton: "Reset password",
    recoveryBusy: "Verifying...",
    recoverySuccess: "Password reset. You can now log in.",
    recoveryBackToLogin: "Back to login",
    invalid_recovery_token: "Incorrect or invalid code.",
    recovery_token_expired: "The code has expired. Generate a new one on the server.",
    recovery_token_used: "This code has already been used.",
    recovery_token_exhausted: "Too many attempts. Generate a new code on the server.",
  },
  admin: {
    title: "Admin Panel",
    subtitle: "AI infrastructure, connections, models, costs, and governance.",
    ownerBadge: "Owner Exclusive",
    unauthorizedTitle: "Restricted Access",
    unauthorizedBody: "The Admin Panel is strictly reserved for the studio Owner.",
    returnHome: "Back to Home",
    tabs: {
      overview: "Overview",
      users: "Members & Invitations",
      connections: "APIs & Connections",
      models: "Models",
      lab: "AI Lab",
      budget: "Costs & Budgets",
      logs: "Logs",
    },
    sections: {
      overview: {
        title: "Infrastructure Overview",
        desc: "Centralized architecture with strict separation between Creative Director and generative media production.",
        sessionTitle: "Active Administrative Session",
        sessionDesc: "Authenticated with official Owner credentials.",
        domainsTitle: "Configured AI Domains",
        cdDomainTitle: "Creative Director AI",
        cdDomainDesc: "Cinematic reasoning, scene understanding, prompt crafting, and continuity evaluation. Independent LLM connections.",
        genDomainTitle: "Audiovisual Generation",
        genDomainDesc: "Image and video synthesis, visual references, and cost reconciliation. Decoupled execution engine with mandatory human preflight approval.",
      },
      connections: {
        title: "APIs & Connections",
        desc: "Secure credential management segregated by AI domain.",
        pendingNotice: "Connection manager currently in activation phase (Stage 2 for Creative Director, Stage 4 for Generation). Credentials will be stored encrypted on the backend with an independent mandatory master key and anti-SSRF protection.",
        cdTab: "Creative Director (LLMs)",
        genTab: "Audiovisual Generation",
        cdNotice: "Dedicated connections for reasoning models (DeepSeek, Qwen/Alibaba, MiniMax, and custom APIs). Zero access to generation API keys.",
        genNotice: "Media synthesis connections. APIMart remains fully operational as the active provider without alterations.",
        statuses: {
          not_configured: "No credentials",
          configured: "Key encrypted in vault",
          credentials_verified: "Credentials verified",
          inference_verified: "Inference verified",
          error: "Connection error",
        },
      },
      models: {
        title: "Model Catalog",
        desc: "Default model assignment and contractual capability inspection.",
        pendingNotice: "Module in activation phase. Will allow setting default models for script, image, and video without altering existing verified capabilities.",
      },
      lab: {
        title: "AI Lab (Creative Director)",
        desc: "Comparative evaluation of cinematic reasoning models under identical benchmarks.",
        pendingNotice: "Module in activation phase (Stage 3). Evaluates latency, tokens, and spatial consistency (Corolla benchmark case) without running billable video generation.",
      },
      budget: {
        title: "Costs & Budgets",
        desc: "Accounting supervision separated by consumption type.",
        pendingNotice: "Module in activation phase. Accurately segregates actual spend into three independent buckets: LLM Reasoning, Image Generation, and Video Generation.",
        bucket1: "Creative Director Spend (LLM Tokens)",
        bucket2: "Image Generation Spend",
        bucket3: "Video Generation Spend",
      },
      logs: {
        title: "Audit Logs",
        desc: "Infrastructure operations and security event history.",
        pendingNotice: "Module in activation phase. Records connection events and administrative changes without exposing credentials.",
      },
    },
    pendingTag: "Pending Activation",
  },
  continuity: {
    title: "Continuity Inspector",
    subtitle: "Interactive supervision of cinematic raccord and narrative coherence.",
    disclaimer: "Textual inspection of prompts and canon · No pixel analysis or computer vision",
    categories: {
      real_contradiction: "Real contradiction",
      possible_risk: "Possible risk",
      missing_information: "Missing information",
      registered_unused_asset: "Unused reference",
    },
    filterAll: "Entire project",
    filterScene: "This scene",
    filterPending: "Pending",
    filterDecided: "Decided",
    pendingCount: (n) => `${n} pending`,
    noObservations: "No continuity issues detected.",
    noSceneObservations: "No pending continuity issues for this scene.",
    cleanState: "No contradictions detected in the reviewed information",
    cleanStateDesc: "The analysis reviews text and metadata; this does not guarantee visual continuity of the generated frames.",
    affectedScenes: "Affected scenes",
    evidence: "Raccord evidence",
    canonSource: "Canonical source",
    recommendation: "Consultative recommendation",
    proposedChange: "Proposed correction",
    beforeText: "Before (current)",
    afterText: "After (proposed)",
    applyProposal: "Apply proposed change",
    appliedSuccess: "Proposed change applied to scene.",
    markReviewed: "Mark as reviewed",
    dismiss: "Dismiss",
    restore: "Restore to pending",
    statusReviewed: "Reviewed",
    statusDismissed: "Dismissed",
    statusApplied: "Applied",
    attentionTitle: "Requires attention",
    informativeTitle: "Cast information and advisory notes",
    possibleUnlinkedRef: "Possible unlinked reference",
    helpTitle: "Actions guide",
    filterSceneHint: "Switch scope to show only observations for the current shot.",
    filterAllHint: "Switch scope to show observations for the whole project.",
    filterPendingHint: "Filter by review status: pending.",
    filterDecidedHint: "Filter by review status: decided.",
    markReviewedHint: "Records that the notice was reviewed; does not modify prompt or approve the shot.",
    dismissHint: "Dismisses the observation without modifying the scene.",
    restoreHint: "Restores the observation to pending.",
    applyProposalHint: "Modifies the prompt with the proposed text upon your explicit action.",
    restoreTitle: "Restore",
    applyProposalTitle: "Apply proposal",
    requestFix: "Request correction",
    requestFixHint: "Prepares a correction draft in the Assistant chat linked to this shot.",
    analysisScope: "Analysis scope",
    singleSceneReviewedBrief: "Prompt reviewed without internal inconsistencies.",
    viewFullPrompt: "View full text",
    hideFullPrompt: "Hide text",
    collapseInformative: "Collapse info and notes",
  },
};

export default en;
